import { useEffect, useRef } from "react";

import AlertFilled from "chameleon/icons/alert-filled";
import CircleCheckFilled from "chameleon/icons/circle-check-filled";
import InfoCheckFilled from "chameleon/icons/info-check-filled";
import { Text } from "chameleon/lib/components";

import * as styles from "./alert.css";

interface AlertProps {
  /**
   * Variant styling changes color
   */
  variant: "Error" | "Success" | "Info";
  /**
   * Title is not necessary but usable
   */
  title?: string | null;
  /**
   * Core description
   */
  description: string;
}

/**
 *
 * @example
 * <Alert variant="Error" title="Error Title" description="Error Description" />
 */
export const Alert = ({
  variant = "Error",
  title,
  description,
}: AlertProps) => {
  const variantIconMap = {
    Error: <AlertFilled color="critical" role="presentation" />,
    Success: <CircleCheckFilled color="positive" role="presentation" />,
    Info: <InfoCheckFilled color="info" role="presentation" />,
  };

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, []);

  return (
    <section
      role="alert"
      className={`${styles.card} ${
        variant === "Error"
          ? styles.errorCard
          : variant === "Info"
            ? styles.infoCard
            : styles.successCard
      }`}
      data-testid="alert"
      ref={sectionRef}
    >
      {variantIconMap[variant]}
      <div className={styles.cardContentWrapper}>
        {title && ( // only show title if it is supplied, otherwise nothing
          <Text
            data-testid="alertTitle"
            styleVariant="heading4"
            colorVariant={
              variant === "Error"
                ? "critical"
                : variant === "Info"
                  ? "info"
                  : "positive"
            }
          >
            {title}
          </Text>
        )}
        <Text styleVariant="body" colorVariant="primaryText">
          {description}
        </Text>
      </div>
    </section>
  );
};
